"use client";

import { create } from "zustand";
import { FEATURE_FLAGS } from "@/lib/feature-flags";
import type { TourId } from "./registry";

const STORAGE_PREFIX = "arbi:tours:v1";

function storageKey(userId: string | null) {
  return `${STORAGE_PREFIX}:${userId ?? "anon"}`;
}

function loadSeen(userId: string | null): Partial<Record<TourId, boolean>> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Partial<Record<TourId, boolean>>;
    }
    return {};
  } catch {
    return {};
  }
}

function saveSeen(
  userId: string | null,
  seen: Partial<Record<TourId, boolean>>,
) {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(seen));
  } catch {
    // Storage full / unavailable, so tour completion simply won't persist.
  }
}

interface ToursStore {
  /** True once `hydrate` has read persisted state for the current user. */
  hydrated: boolean;
  userId: string | null;
  seen: Partial<Record<TourId, boolean>>;
  activeTourId: TourId | null;
  step: number;
  /**
   * A replay request (e.g. from Getting Started). The area's auto-start hook
   * consumes it once the page is ready, even if the tour was already seen.
   */
  requestedTourId: TourId | null;
  /**
   * Tour for the screen currently on view, registered by its `useAutoStartTour`
   * mount. Drives the navbar "replay tour" button; null when the page has none.
   */
  areaTourId: TourId | null;
  hydrate: (userId: string | null) => void;
  start: (tourId: TourId) => void;
  next: () => void;
  back: () => void;
  goTo: (step: number) => void;
  /** Close the tour and mark it seen (one-time tours: skip also counts). */
  finish: () => void;
  request: (tourId: TourId) => void;
  clearRequest: () => void;
  setAreaTour: (tourId: TourId | null) => void;
}

export const useToursStore = create<ToursStore>((set, get) => ({
  hydrated: false,
  userId: null,
  seen: {},
  activeTourId: null,
  step: 0,
  requestedTourId: null,
  areaTourId: null,
  hydrate: (userId) => {
    const state = get();
    if (state.hydrated && state.userId === userId) return;
    set({ userId, seen: loadSeen(userId), hydrated: true });
  },
  start: (tourId) => set({ activeTourId: tourId, step: 0 }),
  next: () => set((state) => ({ step: state.step + 1 })),
  back: () => set((state) => ({ step: Math.max(0, state.step - 1) })),
  goTo: (step) => set({ step: Math.max(0, step) }),
  finish: () => {
    const { activeTourId, seen, userId } = get();
    if (!activeTourId) return;
    const nextSeen = { ...seen, [activeTourId]: true };
    saveSeen(userId, nextSeen);
    set({ activeTourId: null, step: 0, seen: nextSeen });
  },
  request: (tourId) => set({ requestedTourId: tourId }),
  clearRequest: () => set({ requestedTourId: null }),
  setAreaTour: (tourId) => set({ areaTourId: tourId }),
}));

/** Imperatively start a tour right now (targets must already be mounted). */
export function startTour(tourId: TourId): void {
  if (!FEATURE_FLAGS.tours) return;
  useToursStore.getState().start(tourId);
}

/**
 * Request a tour replay. The destination page's `useAutoStartTour` picks the
 * request up once the page is ready, so this is safe to call before or during
 * navigation.
 */
export function requestTour(tourId: TourId): void {
  if (!FEATURE_FLAGS.tours) return;
  useToursStore.getState().request(tourId);
}
